import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { MEDIA, BUSINESS } from "../../lib/business";

import capsulesImg from "../../assets/hero/capsules.jpg";
import tabletsImg from "../../assets/hero/tablets.jpg";
import powdersImg from "../../assets/hero/powders.jpg";
import packagingImg from "../../assets/hero/packaging.jpg";

const SLIDES = [
  { key: "capsules", label: "Capsules", src: capsulesImg, alt: "Yellow capsules on a high-precision filling line at White Bull Global" },
  { key: "tablets", label: "Tablets", src: tabletsImg, alt: "Tablets travelling along a polished conveyor during in-line inspection" },
  { key: "powders", label: "Powders", src: powdersImg, alt: "Pharmaceutical-grade powder blend on a White Bull Global production line" },
  { key: "packaging", label: "Packaging", src: packagingImg, alt: "BIOMIMIC Shudh Shilajit bottles on the automated capping and labelling line at White Bull Global" },
  { key: "laboratory", label: "Laboratory", src: MEDIA.supplementJar, alt: "White Bull Global in-house laboratory and quality testing" },
];

const SLIDE_DURATION_MS = 4500;

export const Hero = ({ onQuoteClick }) => {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotionRef = useRef(false);

  // Detect reduced-motion preference once (and react to changes).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mq.matches;
    const onChange = (e) => {
      reducedMotionRef.current = e.matches;
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Pause when tab is hidden so we don't burn cycles or flash a stale frame on return.
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Auto-advance.
  useEffect(() => {
    if (paused) return;
    if (reducedMotionRef.current) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, [paused]);

  const goTo = useCallback((i) => setActive(i), []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background gold texture */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage: `url(${MEDIA.heroGold})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 wbg-hero-overlay" aria-hidden />
      <div className="absolute inset-0 wbg-grain" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="wbg-rise overline flex items-center gap-3">
            <span className="gold-divider" />
            Nutraceutical · Sport Nutrition · Healthcare
          </div>

          <h1
            data-testid="hero-headline"
            className="wbg-rise wbg-rise-delay-1 font-serif-display text-[2.5rem] sm:text-5xl lg:text-[4.25rem] leading-[1.05] text-[#1A1A1A] mt-6 max-w-3xl"
          >
            Purity is{" "}
            <span className="italic text-[#C5A059]">Priority.</span>
            <br />
            Crafted in India.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="wbg-rise wbg-rise-delay-2 mt-6 text-base md:text-lg text-[#595959] max-w-xl leading-relaxed"
          >
            White Bull Global is a precision nutraceutical and supplement
            manufacturer based in Sonipat, Haryana — producing healthcare,
            fitness and sport nutrition formulations built on FSSAI, GMP and
            AYUSH certified standards.
          </p>

          <div className="wbg-rise wbg-rise-delay-3 mt-9 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-get-quote"
              onClick={onQuoteClick}
              className="btn-gold rounded-sm h-12 px-7 text-sm font-semibold tracking-wider inline-flex items-center gap-2"
            >
              Get a Quote <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              data-testid="hero-view-services"
              onClick={() => scrollTo("services")}
              className="btn-ghost-ink rounded-sm h-12 px-7 text-sm font-semibold tracking-wider bg-transparent"
            >
              View Services
            </Button>
          </div>

          <div className="wbg-rise wbg-rise-delay-4 mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#2A2A2A]">
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              <span className="tracking-wide">FSSAI · GMP · AYUSH Certified</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              <span className="tracking-wide">
                {BUSINESS.city}, {BUSINESS.state}
              </span>
            </div>
          </div>
        </div>

        {/* Right visual — category slideshow */}
        <div
          className="lg:col-span-5 relative wbg-rise wbg-rise-delay-2"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="hidden md:block absolute -top-6 -right-6 w-28 h-28 border border-[#D4AF37] pointer-events-none"
            aria-hidden
          />
          <div
            className="hidden md:block absolute -bottom-8 -left-8 w-24 h-24 bg-[#D4AF37] pointer-events-none"
            aria-hidden
          />
          <div
            className="relative aspect-[4/5] bg-white border border-[#E5E5E5] overflow-hidden"
            aria-roledescription="carousel"
            aria-label="White Bull Global manufacturing capabilities"
          >
            {/* Stacked, cross-fading slides */}
            {SLIDES.map((slide, i) => (
              <img
                key={slide.key}
                src={slide.src}
                alt={slide.alt}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out"
                style={{ opacity: i === active ? 1 : 0 }}
                aria-hidden={i !== active}
              />
            ))}

            {/* Caption strip with interactive category labels */}
            <div className="absolute bottom-0 left-0 right-0 px-6 py-5 bg-white/95 backdrop-blur-sm border-t border-[#E5E5E5]">
              <div className="overline text-[10px]">In-house manufacturing</div>
              <div
                className="font-serif-display text-base sm:text-lg text-[#1A1A1A] mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1"
                role="tablist"
                aria-label="Manufacturing categories"
              >
                {SLIDES.map((slide, i) => {
                  const isActive = i === active;
                  return (
                    <span key={slide.key} className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={`hero-slide-${slide.key}`}
                        onClick={() => goTo(i)}
                        data-testid={`hero-slide-tab-${slide.key}`}
                        className="font-serif-display text-base sm:text-lg leading-none transition-all duration-500 ease-out focus:outline-none focus-visible:underline underline-offset-4 decoration-[#D4AF37]"
                        style={{
                          color: isActive ? "#1A1A1A" : "#B5B5B5",
                          fontWeight: isActive ? 600 : 400,
                          letterSpacing: isActive ? "0.005em" : "0",
                        }}
                      >
                        {slide.label}
                      </button>
                      {i < SLIDES.length - 1 && (
                        <span className="text-[#D4D4D4]" aria-hidden>
                          ·
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>

              {/* Slim progress bar — restarts on every slide change via key */}
              <div className="mt-3 h-[2px] w-full bg-[#EFEAD9] overflow-hidden">
                <div
                  key={`bar-${active}-${paused ? "p" : "r"}`}
                  className="h-full bg-[#D4AF37] wbg-hero-progress"
                  style={{
                    animationDuration: `${SLIDE_DURATION_MS}ms`,
                    animationPlayState: paused ? "paused" : "running",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
